import * as Exact from "./decimal.js";
import type { AccountRef, CalcFee, Decimal } from "./api.js";

// Tobacco municipality fees (migration 049, docs/tobacco-fees.md). Off by default for every organization. The
// defaults (100 % and SAR 25.00) are the business defaults Delta was given, not independently verified legal rates.

export type TobaccoMinimumBasis = "base_unit" | "selling_unit";
export type TobaccoShortfall = "refuse" | "minimum_on_top";
export type TobaccoInvoiceDiscount = "all_lines" | "exclude_fee_lines";

export interface TobaccoSettingsDto {
  enabled: boolean;
  /** Licence identifiers are digit strings; leading zeros are kept. */
  licenceNumber: string; municipality: string; licenceExpiresOn: string | null;
  feePct: Decimal; minimumFee: Decimal; priceIncludesFee: boolean;
  liabilityAccountId: string | null; liabilityAccount: AccountRef | null;
  /** The three open policies; null until the owner decides (the feature cannot be switched on before). */
  minimumBasis: TobaccoMinimumBasis | null; inclusiveShortfall: TobaccoShortfall | null; invoiceDiscount: TobaccoInvoiceDiscount | null;
  updatedAt: string | null; updatedByName: string | null;
  /** Items currently marked subject to the fee. */
  itemsCount: number;
}
export interface TobaccoSettingsInput {
  enabled: boolean; licenceNumber: string; municipality: string; licenceExpiresOn: string | null;
  feePct: string; minimumFee: string; priceIncludesFee: boolean; liabilityAccountId: string | null;
  minimumBasis: TobaccoMinimumBasis | null; inclusiveShortfall: TobaccoShortfall | null; invoiceDiscount: TobaccoInvoiceDiscount | null;
  reason?: string;
}
/** What an invoice keeps of the settings it was worked out under. */
export interface TobaccoSnapshot {
  feePct: string; minimumFee: string; priceIncludesFee: boolean; minimumBasis: TobaccoMinimumBasis; inclusiveShortfall: TobaccoShortfall;
  invoiceDiscount: TobaccoInvoiceDiscount; liabilityAccountId: string; licenceNumber: string;
  /**
   * Stored with the invoice, not settings: how much the minimum fee added on top of fee-inclusive prices raised its
   * total (case b), and the total the user confirmed for it (null: not confirmed; it cannot be completed so).
   */
  raised?: string; confirmedTotal?: string | null;
}
/** The fee settings a sales screen or the register needs to show the same figures the server will work out. */
export interface TobaccoCalcSettings {
  enabled: boolean; feePct: Decimal; minimumFee: Decimal; priceIncludesFee: boolean;
  minimumBasis: TobaccoMinimumBasis | null; inclusiveShortfall: TobaccoShortfall | null; invoiceDiscount: TobaccoInvoiceDiscount | null;
}

export interface TobaccoReportRow {
  invoiceId: string; kind: "sale" | "sale_refund"; code: string; at: string;
  /** The row of a reversal (F-2): the invoice again, negated, on its reversal date. */
  reversal: boolean;
  branch: { id: string; nameEn: string; nameAr: string }; party: string | null;
  /** The product amount of the lines that charged the fee, and the fee: sales positive, refunds and reversals negative. */
  base: Decimal; fee: Decimal;
}
export interface TobaccoReportDto {
  from: string | null; to: string | null; branchId: string | null;
  rows: TobaccoReportRow[];
  /** Fees on sales, fees returned by refunds, and the difference (reversals counted against what they reverse). */
  collected: Decimal; refunded: Decimal; net: Decimal;
  /** The liability account(s) the fees were posted to, and each one's balance (credit less debit) at the end of the period, all sources. */
  accounts: { account: AccountRef; balance: Decimal }[];
}

/** The settings a line's fee is worked out under: an invoice's snapshot, or (on a sales screen) the current settings. */
export type TobaccoFeeSettings = Pick<TobaccoSnapshot, "feePct" | "minimumFee" | "priceIncludesFee"> & {
  minimumBasis: TobaccoMinimumBasis | null; inclusiveShortfall: TobaccoShortfall | null;
};

/**
 * The fee input of one line (shared invoiceCalc), or null when the line charges none: no settings (the fee is off, or
 * the invoice was issued without it), or an item not subject to the fee. The minimum counts base units (qty × pieces
 * of the selling unit) or selling units (qty), as the organization's policy says (docs/tobacco-fees.md, case a).
 */
export function tobaccoFeeOf(s: TobaccoFeeSettings | null, subject: boolean, qty: string, pieces: string): CalcFee | null {
  if (!s || !subject || !s.minimumBasis || !s.inclusiveShortfall) return null;
  const units = s.minimumBasis === "base_unit" ? Exact.fmt(Exact.mul(Exact.dec(qty), Exact.dec(pieces))) : qty;
  return { pct: s.feePct, minimum: s.minimumFee, units, inclusive: s.priceIncludesFee, shortfall: s.inclusiveShortfall };
}
